import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { 
  Notification, 
  NotificationListParams 
} from '../types/notification';
import { 
  fetchNotifications, 
  markNotificationAsRead 
} from '../services/notificationApi';

export const useNotifications = (params: NotificationListParams = {}) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // params를 메모이제이션하여 무한루프 방지
  const memoizedParams = useMemo(() => ({ 
    ...params, 
    isRead: false // 읽지 않은 알림만 조회
  }), [params.startDate, params.endDate]);

  // 알림 목록 조회 (읽지 않은 알림만)
  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchNotifications(memoizedParams);
      setNotifications(response.data);
      setUnreadCount(response.data.length); // 읽지 않은 알림 개수는 응답 데이터 길이와 동일
    } catch (err) {
      setError(err instanceof Error ? err.message : '알림을 불러오는데 실패했습니다.');
      console.error('알림 목록 조회 실패:', err);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, memoizedParams]);

  // 알림 읽음 처리
  const markAsRead = useCallback(async (notificationId: number) => {
    try {
      await markNotificationAsRead(notificationId);
      
      // 읽음 처리된 알림을 목록에서 제거
      setNotifications(prev => prev.filter(notification => 
        notification.notification_id !== notificationId
      ));
      
      // 읽지 않은 알림 개수 감소
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (err) {
      console.error('알림 읽음 처리 실패:', err);
      throw err;
    }
  }, []);

  // 새로고침 함수
  const refresh = useCallback(() => {
    loadNotifications();
  }, [loadNotifications]);

  // 초기 로드 및 인증 상태 변경 시 로드
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    refresh,
  };
};
